import firebase from "firebase/app";

import { Divider, Segment } from "semantic-ui-react";

import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import articlePublicationStatuses from "astrid-firestore/src/api/articles/constants/articlePublicationStatuses";
import articleStatuses from "astrid-firestore/src/api/articles/constants/articleStatuses";
import articleTypes from "astrid-firestore/src/api/articles/constants/articleTypes";
import createArticle from "astrid-firestore/src/api/articles/createArticle";
import { Article } from "astrid-firestore/src/api/articles/types/Article";
import { ArticleMetaData } from "astrid-firestore/src/api/articles/types/ArticleMetaData";
import updateArticle from "astrid-firestore/src/api/articles/updateArticle";
import getArticleDistributionPresets from "astrid-firestore/src/api/distribution/getArticleDistributionPresets";
import softDeleteDocument from "astrid-firestore/src/api/documents/softDeleteDocument";
import getDefaultValues from "astrid-firestore/src/types/getDefaultValues";

import Flex from "../../../../components/Flex/Flex";
import Button from "../../../ui/components/Buttons/Button";
import ConfirmationButton from "../../../ui/components/ConfirmationButton/ConfirmationButton";
import ISBNInput from "../../../ui/components/ISBNInput/ISBNInput";
import SidebarPortal from "../../../ui/components/SidebarPortal/SidebarPortal";
import Toast from "../../../ui/components/Toast/Toast";

import { useProfile } from "../../../authentication/state/profile";
import useHasRight from "../../../authorization/hooks/useHasRight";
import DistributionCountriesCheckboxTable from "../../../distribution/components/DistributionCountriesCheckboxTable/DistributionCountriesCheckboxTable";
import DistributionPriceForm from "../../../distribution/components/DistributionPrice/DistributionPriceForm";
import Form from "../../../forms/components/Form/Form";
import FormValidationMessageSticky from "../../../forms/components/FormValidationMessage/FormValidationMessageSticky";
import useSimpleForm from "../../../forms/hooks/useSimpleForm";

import useAccessibilityOptions from "../../hooks/useAccessibilityOptions";
import useArticleTypeOptions from "../../hooks/useArticleTypeOptions";
import useEpubVersionOptions from "../../hooks/useEpubVersionOptions";
import useHazardOrWarningOptions from "../../hooks/useHazardOrWarningOptions";

import ArticleEditChannel from "./components/ArticleEditChannel";
import ArticleEditCover from "./components/ArticleEditCover";
import ArticleEditDates from "./components/ArticleEditDates";
import ArticleEditImports from "./components/ArticleEditImports";
import ArticleEditOverrides from "./components/ArticleEditOverrides";

const defaultValues = getDefaultValues(ArticleMetaData);

export default function ArticleEdit({
	work,
	article,
	channels,
	publisher,
	production,
	overlay,
	onSuccess: onSuccessProp,
	onClose,
}) {
	const { t } = useTranslation();
	const hasRight = useHasRight();
	const user = useProfile();

	const { form, watch, formState, setValue, onSubmit } = useSimpleForm({
		schema: Article,
		defaultValues: {
			...Object.fromEntries(Object.entries(defaultValues).map(([key, value]) => [key, article[key] || value])),
			type: article.type || null,
			channelIds: article.channelIds || [],
			channels: Object.entries(article.channels || {}).reduce(
				(acc, [key, channel]) => ({
					...acc,
					[key]: {
						announcementDate: channel.announcementDate || null,
						releaseDate: channel.releaseDate || null,
						takedownDate: channel.takedownDate || null,
						deliveryTypes: channel.deliveryTypes || [],
						prices: channel.prices || [],
						countries: channel.countries || [],
						subChannels: channel.subChannels || [],
					},
				}),
				{},
			),
			imports: article.imports
				? {
						enabled: article.imports.enabled || false,
						autoDelivery: article.imports.autoDelivery || false,
				  }
				: null,
		},
		onSubmit(data, event) {
			if (!article.exists) {
				return createArticle(
					firebase,
					{
						ref: article.ref,
						...data,
						status: event.target.name === "draft" ? articleStatuses.DRAFT : articleStatuses.READY,
						work,
						publisher,
						production,
					},
					{ user },
				);
			}
			return updateArticle(firebase, { ref: article.ref, ...data }, { user, debug: false });
		},
		disabled: !!article.imports,
		onSuccess: (result) => {
			onSuccessProp?.(result);
			onClose();
		},
		onError: (error) => {
			if (error.message.includes("same ISBN already exists")) {
				form.setError("isbn", {
					type: "manual",
					message: t("isbnAlreadyExists", "An article with the same ISBN already exists"),
				});
			}
		},
	});

	const [type, language, channelIds] = watch(["type", "language", "channelIds"]);

	const articleTypeOptions = useArticleTypeOptions();
	const epubVersionOptions = useEpubVersionOptions();
	const accessibilityOptions = useAccessibilityOptions();
	const hazardOrWarningOptions = useHazardOrWarningOptions();

	const { presetChannels, applyPresets } = useMemo(() => {
		return getArticleDistributionPresets({
			article: { ...article, type, language },
			publisher,
			channels,
		});
	}, [article, type, language, publisher, channels]);

	const focus = !article.exists || formState.isDirty;

	useEffect(() => {
		if (!article.exists) {
			const channels = applyPresets();

			setValue("channelIds", Object.keys(channels));
			setValue("channels", channels);
		}
	}, [applyPresets, article.exists, setValue]);

	return (
		<SidebarPortal width={500} focus={focus} open overlay={overlay} onClose={onClose}>
			<Form form={form}>
				<FormValidationMessageSticky />

				<Segment basic padded style={{ margin: 0 }}>
					<Flex gap={20} alignItems="flex-start" style={{ marginBottom: "1em" }}>
						<ArticleEditCover article={article} disabled={formState.disabled} />

						<div style={{ flex: 1 }}>
							<Form.Select
								fluid
								name="type"
								label={t("type", "Type")}
								options={articleTypeOptions}
								disabled={article.status !== articleStatuses.DRAFT}
							/>

							<ISBNInput
								fluid
								name="isbn"
								label={t("isbn", "ISBN")}
								disabled={article.status !== articleStatuses.DRAFT}
							/>
						</div>
					</Flex>

					<Form.Group widths="equal">
						<ArticleEditDates />

						{article.type !== articleTypes.EBOOK && (
							<Form.DurationInput name="duration" label={t("duration", "Duration") + " (hhh:mm:ss)"} />
						)}

						{article.type === articleTypes.EBOOK && (
							<Form.NumberInput name="pages" label={t("pages", "Pages")} />
						)}

						{article.type === articleTypes.EBOOK && (
							<Form.Select
								name="epubVersion"
								search
								disabled={article.artifact}
								label={t("epubVersion", "EPUB version")}
								options={epubVersionOptions}
							/>
						)}
					</Form.Group>

					{article.type === articleTypes.EBOOK && (
						<Form.Select
							multiple
							search
							name="accessibility"
							label={t("accessibility", "Accessibility")}
							options={accessibilityOptions}
						/>
					)}

					<Form.Select
						name="hazardsOrWarnings"
						multiple
						search
						fluid
						label={t("hazardsOrWarnings", "Hazards or warnings")}
						options={hazardOrWarningOptions}
					/>

					<DistributionPriceForm name="prices" publisher={publisher} />

					<DistributionCountriesCheckboxTable name="countries" />

					{presetChannels.length > 0 && (
						<Divider section horizontal>
							{t("channels", "Channels")} {channelIds.length}/{presetChannels.length}
						</Divider>
					)}

					{presetChannels.map((channel) => (
						<ArticleEditChannel key={channel.id} channel={channel} publisher={publisher} />
					))}

					<ArticleEditOverrides work={work} article={article} publisher={publisher} />

					{article.imports && hasRight("super") && <ArticleEditImports article={article} />}

					<Divider section horizontal>
						{t("actions", "Actions")}
					</Divider>

					<Flex justifyContent="center">
						<ConfirmationButton
							negative
							text={t("confirmArticleDelete", "Are you sure you want to delete article {{name}}?", {
								name: article.name,
							})}
							onConfirm={() => {
								softDeleteDocument(firebase, article, user);
							}}
							disabled={
								article.publicationStatus === articlePublicationStatuses.PUBLISHED ||
								article.publicationStatus === articlePublicationStatuses.ANNOUNCED
							}
						>
							{t("delete", "Delete")}
						</ConfirmationButton>
					</Flex>
				</Segment>

				<Form.Blocker />
			</Form>

			<Toast open>
				<Button onClick={onClose}>{t("cancel", "Cancel")}</Button>
				{!article.exists && (
					<Button
						name="draft"
						onClick={onSubmit}
						disabled={formState.isSubmitting || !formState.isDirty}
						loading={formState.isSubmitting}
					>
						{t("saveDraft", "Save draft")}
					</Button>
				)}

				<Button
					primary
					onClick={onSubmit}
					disabled={formState.isSubmitting || !formState.isDirty}
					loading={formState.isSubmitting}
				>
					{article.exists ? t("saveChanges", "Save Changes") : t("createArticle", "Create Article")}
				</Button>
			</Toast>
		</SidebarPortal>
	);
}
